import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import {
  ClassificationItem,
  ClassificationStats,
  LowConfidenceStats,
  PaginatedTrainingExamplesResponse,
  ThresholdConfig,
  ThresholdEntry,
  annotateClassification,
  createTrainingExample,
  deleteTrainingExample,
  getClassificationStats,
  getLowConfidenceStats,
  getThresholdConfig,
  listClassifications,
  listTrainingExamples,
  updateThresholdConfig,
} from "@/services/classificationsApi";

// Une entrée par catégorie : le nom, son raccourci clavier dans la file
// d'annotation, et sa description lisible. Les trois vivent ENSEMBLE
// volontairement — c'était auparavant trois structures appariées par position,
// et une longueur qui divergeait échouait en silence (badge vide, raccourci
// mort, voire onAnnotate(undefined) envoyé à l'API). Ici TypeScript exige les
// trois champs, donc l'oubli n'est plus représentable.
//
// Ordre canonique = celui de back/src/features/analysis/schema.ResponseCategoryEnum,
// pour que la liste se lise dans le même ordre que les logits du modèle.
// Touche S (passer) gérée à part — ce n'est pas une catégorie.
const LABELS = [
  { name: "Type A", key: "1", description: "Intéressé" },
  { name: "Type A.2", key: "2", description: "Intéressé sous condition / demande une précision" },
  { name: "Type B", key: "3", description: "Intéressé + disponibilité" },
  { name: "Type C.1", key: "4", description: "Pas à l'écoute — poste actuel" },
  { name: "Type C.2", key: "5", description: "Pas à l'écoute — pas le moment" },
  { name: "Type C.3", key: "6", description: "Pas à l'écoute — autre raison" },
  { name: "Type D.1", key: "7", description: "Pas intéressé — rêve pro différent" },
  { name: "Type D.2", key: "8", description: "Pas intéressé — non précisé" },
  { name: "Type D.3", key: "9", description: "Pas intéressé — secteur" },
  { name: "Type E.1", key: "0", description: "Hors-sujet" },
  { name: "Type E.2", key: "-", description: "Vide / non interprétable" },
] as const;

// Lookup par nom, pour les labels qui arrivent de l'API (`predicted_label`) et
// ne sont donc pas forcément dans LABELS. Dérivé : pas de seconde liste à tenir.
const LABEL_DESCRIPTIONS: Record<string, string> = Object.fromEntries(
  LABELS.map((label) => [label.name, label.description]),
);

export default function ClassificationsPage() {
  const navigate = useNavigate();

  const [stats, setStats] = useState<ClassificationStats | null>(null);
  const [queue, setQueue] = useState<ClassificationItem[]>([]);
  const [queueTotal, setQueueTotal] = useState<number>(0);
  const [loadingQueue, setLoadingQueue] = useState<boolean>(false);
  const [annotating, setAnnotating] = useState<boolean>(false);

  const [examples, setExamples] = useState<PaginatedTrainingExamplesResponse | null>(null);
  const [examplesPage, setExamplesPage] = useState<number>(1);
  const [newText, setNewText] = useState<string>("");
  const [newLabel, setNewLabel] = useState<string>(LABELS[0].name);
  const [creating, setCreating] = useState<boolean>(false);

  // Seuils de confiance — draft/dirty/save (même pattern que quotas/page.tsx).
  // Les inputs sont des strings (saisie libre) validées au save.
  const [thresholdConfig, setThresholdConfig] = useState<ThresholdConfig | null>(null);
  const [lcStats, setLcStats] = useState<LowConfidenceStats | null>(null);
  const [globalDraft, setGlobalDraft] = useState<string>("");
  const [overrideDrafts, setOverrideDrafts] = useState<Record<string, string | null>>({});
  const [savingThresholds, setSavingThresholds] = useState<boolean>(false);

  const current = queue.length > 0 ? queue[0] : null;

  const resetThresholdDrafts = useCallback((cfg: ThresholdConfig) => {
    setGlobalDraft(String(cfg.global_threshold));
    const drafts: Record<string, string | null> = {};
    for (const { name } of LABELS) {
      drafts[name] = name in cfg.overrides ? String(cfg.overrides[name]) : null;
    }
    setOverrideDrafts(drafts);
  }, []);

  const refreshThresholds = useCallback(async () => {
    try {
      const cfg = await getThresholdConfig(navigate);
      setThresholdConfig(cfg);
      resetThresholdDrafts(cfg);
    } catch {
      // table pas encore migrée / backoffice indisponible : la card affiche un état vide
    }
    try {
      setLcStats(await getLowConfidenceStats(navigate, 7));
    } catch {
      // stats décoratives
    }
  }, [navigate, resetThresholdDrafts]);

  useEffect(() => {
    refreshThresholds();
  }, [refreshThresholds]);

  const thresholdsDirty = (() => {
    if (!thresholdConfig) return false;
    if (globalDraft !== String(thresholdConfig.global_threshold)) return true;
    return LABELS.some(({ name }) => {
      const before = name in thresholdConfig.overrides ? String(thresholdConfig.overrides[name]) : null;
      return (overrideDrafts[name] ?? null) !== before;
    });
  })();

  const onSaveThresholds = async () => {
    if (!thresholdConfig || savingThresholds) return;
    const entries: ThresholdEntry[] = [];
    const parse = (raw: string, scope: string): number => {
      const v = Number(raw.replace(",", "."));
      if (!Number.isFinite(v) || v < 0 || v > 1) {
        throw new Error(`${scope} : seuil invalide « ${raw} » (attendu entre 0 et 1)`);
      }
      return v;
    };
    try {
      if (globalDraft !== String(thresholdConfig.global_threshold)) {
        entries.push({ scope: "global", threshold: parse(globalDraft, "global") });
      }
      for (const { name } of LABELS) {
        const before = name in thresholdConfig.overrides ? String(thresholdConfig.overrides[name]) : null;
        const after = overrideDrafts[name] ?? null;
        if (after === before) continue;
        entries.push({ scope: name, threshold: after === null ? null : parse(after, name) });
      }
    } catch (e) {
      toast({ title: "Seuil invalide", description: e instanceof Error ? e.message : "", variant: "destructive" });
      return;
    }
    if (entries.length === 0) return;
    setSavingThresholds(true);
    try {
      await updateThresholdConfig(navigate, entries);
      toast({
        title: "Seuils enregistrés",
        description: "Les pods back appliquent la nouvelle configuration sous ~30 s.",
      });
      await refreshThresholds();
    } catch (e) {
      toast({ title: "Erreur", description: e instanceof Error ? e.message : "Erreur inconnue", variant: "destructive" });
    } finally {
      setSavingThresholds(false);
    }
  };

  const refreshStats = useCallback(async () => {
    try {
      setStats(await getClassificationStats(navigate));
    } catch {
      // silencieux : les stats ne bloquent pas l'annotation
    }
  }, [navigate]);

  const refreshQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const res = await listClassifications(navigate, { page: 1, size: 10, annotated: false });
      setQueue(res.items);
      setQueueTotal(res.total);
    } catch (e) {
      toast({ title: "Erreur", description: e instanceof Error ? e.message : "Erreur inconnue", variant: "destructive" });
    } finally {
      setLoadingQueue(false);
    }
  }, [navigate]);

  const refreshExamples = useCallback(async () => {
    try {
      setExamples(await listTrainingExamples(navigate, { page: examplesPage, size: 10 }));
    } catch (e) {
      toast({ title: "Erreur", description: e instanceof Error ? e.message : "Erreur inconnue", variant: "destructive" });
    }
  }, [navigate, examplesPage]);

  useEffect(() => {
    refreshStats();
    refreshQueue();
  }, [refreshStats, refreshQueue]);

  useEffect(() => {
    refreshExamples();
  }, [refreshExamples]);

  const onAnnotate = useCallback(
    async (label: string) => {
      if (!current || annotating) return;
      setAnnotating(true);
      try {
        const res = await annotateClassification(navigate, current.id, label);
        toast({
          title: label === "SKIP" ? "Passé" : `Annoté ${label}`,
          description: res.training_example_created
            ? "Ajouté au dataset d'entraînement"
            : "Hors dataset (SKIP ou texte introuvable)",
        });
        setQueue((q) => q.slice(1));
        setQueueTotal((t) => Math.max(0, t - 1));
        if (queue.length <= 1) refreshQueue();
        refreshStats();
        refreshExamples();
      } catch (e) {
        toast({ title: "Erreur", description: e instanceof Error ? e.message : "Erreur inconnue", variant: "destructive" });
      } finally {
        setAnnotating(false);
      }
    },
    [annotating, current, navigate, queue.length, refreshExamples, refreshQueue, refreshStats],
  );

  // Raccourcis clavier globaux — désactivés quand un champ de saisie a le focus.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "TEXTAREA" || target.tagName === "INPUT" || target.tagName === "SELECT") return;
      const hit = LABELS.find((label) => label.key === e.key);
      if (hit) {
        e.preventDefault();
        onAnnotate(hit.name);
      } else if (e.key.toLowerCase() === "s") {
        e.preventDefault();
        onAnnotate("SKIP");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onAnnotate]);

  const onCreateExample = async () => {
    if (!newText.trim() || creating) return;
    setCreating(true);
    try {
      await createTrainingExample(navigate, { text: newText.trim(), label: newLabel });
      toast({ title: "Exemple ajouté", description: `${newLabel} — sera fusionné dans le CSV d'entraînement` });
      setNewText("");
      refreshExamples();
      refreshStats();
    } catch (e) {
      toast({ title: "Erreur", description: e instanceof Error ? e.message : "Erreur inconnue", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const onDeleteExample = async (id: number) => {
    try {
      await deleteTrainingExample(navigate, id);
      toast({ title: "Exemple supprimé" });
      refreshExamples();
      refreshStats();
    } catch (e) {
      toast({ title: "Erreur", description: e instanceof Error ? e.message : "Erreur inconnue", variant: "destructive" });
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Classifications</h1>
          </div>
        </header>

        <div className="p-6 space-y-6">
          {/* Statistiques */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Prédictions totales</CardDescription>
                <CardTitle className="text-2xl">{stats?.total ?? "—"}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Annotées</CardDescription>
                <CardTitle className="text-2xl">{stats?.annotated ?? "—"}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Restantes</CardDescription>
                <CardTitle className="text-2xl">{stats?.remaining ?? "—"}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Exemples dataset</CardDescription>
                <CardTitle className="text-2xl">{stats?.training_examples_total ?? "—"}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Seuils de confiance */}
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle>Seuils de confiance</CardTitle>
                {thresholdConfig && !thresholdConfig.gate_enabled ? (
                  <Badge variant="destructive">Gate désactivé (ANALYSIS_CONFIDENCE_GATE_ENABLED=false)</Badge>
                ) : null}
              </div>
              <CardDescription>
                Sous le seuil, la réponse n'est PAS routée automatiquement : le candidat passe en « À faire »
                (revue humaine, badge « À vérifier » dans la messagerie) et aucun message automatique n'est
                envoyé. Seuil global par défaut, surcharge possible par classe. Propagation aux pods back ≤ 30 s.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!thresholdConfig ? (
                <p className="text-sm text-muted-foreground">
                  Configuration indisponible (migration non appliquée ou backoffice injoignable).
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium">Seuil global</span>
                    <Input
                      className="w-24"
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      value={globalDraft}
                      onChange={(e) => setGlobalDraft(e.target.value)}
                    />
                    <span className="text-xs text-muted-foreground">
                      défaut : {thresholdConfig.defaults.global}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="px-3 py-2">Classe</th>
                          <th className="px-3 py-2">Surcharger</th>
                          <th className="px-3 py-2">Seuil</th>
                          <th className="px-3 py-2">Basse conf. (7 j)</th>
                          <th className="px-3 py-2">Conf. moyenne (7 j)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {LABELS.map(({ name: label, description }) => {
                          const draft = overrideDrafts[label] ?? null;
                          const st = lcStats?.stats?.[label];
                          return (
                            <tr key={label} className="border-b">
                              <td className="px-3 py-2">
                                <Badge variant="secondary">{label}</Badge>{" "}
                                <span className="text-muted-foreground">{description}</span>
                              </td>
                              <td className="px-3 py-2">
                                <Switch
                                  checked={draft !== null}
                                  onCheckedChange={(checked) =>
                                    setOverrideDrafts((d) => ({
                                      ...d,
                                      [label]: checked ? globalDraft || String(thresholdConfig.global_threshold) : null,
                                    }))
                                  }
                                />
                              </td>
                              <td className="px-3 py-2">
                                {draft !== null ? (
                                  <Input
                                    className="w-24"
                                    type="number"
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={draft}
                                    onChange={(e) =>
                                      setOverrideDrafts((d) => ({ ...d, [label]: e.target.value }))
                                    }
                                  />
                                ) : (
                                  <span className="text-muted-foreground">global ({globalDraft || "—"})</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {st && st.total > 0
                                  ? `${(st.rate * 100).toFixed(0)} % (${st.low_confidence}/${st.total})`
                                  : "—"}
                              </td>
                              <td className="px-3 py-2">
                                {st?.avg_confidence != null ? `${(st.avg_confidence * 100).toFixed(0)} %` : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" disabled={!thresholdsDirty || savingThresholds} onClick={onSaveThresholds}>
                      {savingThresholds ? "Enregistrement…" : "Enregistrer les seuils"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!thresholdsDirty || savingThresholds}
                      onClick={() => thresholdConfig && resetThresholdDrafts(thresholdConfig)}
                    >
                      Annuler les modifications
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* File d'annotation */}
          <Card>
            <CardHeader>
              <CardTitle>À annoter {queueTotal > 0 ? `(${queueTotal} restants)` : ""}</CardTitle>
              <CardDescription>
                Validez ou corrigez la prédiction du modèle. Raccourcis : 1-9 et 0 pour les labels, S pour passer.
                Chaque label validé alimente le dataset d'entraînement.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingQueue && !current ? (
                <p className="text-sm text-muted-foreground">Chargement…</p>
              ) : !current ? (
                <p className="text-sm text-muted-foreground">
                  Aucune prédiction en attente d'annotation. Les nouvelles réponses candidats apparaîtront ici.
                </p>
              ) : (
                <>
                  <div className="rounded-md border bg-muted/40 p-4">
                    <p className="whitespace-pre-wrap text-sm">{current.message ?? "(texte introuvable)"}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span>Prédiction :</span>
                    <Badge>{current.predicted_label}</Badge>
                    <span>{LABEL_DESCRIPTIONS[current.predicted_label] ?? ""}</span>
                    <span>· confiance {(current.confidence * 100).toFixed(0)} %</span>
                    {current.candidate_name ? <span>· {current.candidate_name}</span> : null}
                    <span>· {new Date(current.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {LABELS.map(({ name: label, key, description }) => (
                      <Button
                        key={label}
                        variant={label === current.predicted_label ? "default" : "outline"}
                        size="sm"
                        disabled={annotating}
                        onClick={() => onAnnotate(label)}
                        className="justify-start"
                      >
                        <span className="mr-2 rounded bg-muted px-1.5 text-xs text-muted-foreground">
                          {key}
                        </span>
                        {label} — {description}
                      </Button>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" disabled={annotating} onClick={() => onAnnotate("SKIP")}>
                    <span className="mr-2 rounded bg-muted px-1.5 text-xs text-muted-foreground">S</span>
                    Passer (indécidable)
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Ajout manuel au dataset */}
          <Card>
            <CardHeader>
              <CardTitle>Ajouter un exemple au dataset</CardTitle>
              <CardDescription>
                Ajoute une ligne au CSV d'entraînement (fusion via scripts/build_training_csv.py). Anonymisez le
                texte : retirez noms et coordonnées.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="Texte de la réponse candidat…"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                rows={3}
              />
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                >
                  {LABELS.map(({ name: label, description }) => (
                    <option key={label} value={label}>
                      {label} — {description}
                    </option>
                  ))}
                </select>
                <Button size="sm" disabled={!newText.trim() || creating} onClick={onCreateExample}>
                  Ajouter au dataset
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Derniers exemples */}
          <Card>
            <CardHeader>
              <CardTitle>Derniers exemples du dataset</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="px-3 py-2">Texte</th>
                      <th className="px-3 py-2">Label</th>
                      <th className="px-3 py-2">Source</th>
                      <th className="px-3 py-2">Par</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {(examples?.items ?? []).map((ex) => (
                      <tr key={ex.id} className="border-b align-top">
                        <td className="max-w-xl whitespace-pre-wrap px-3 py-2">{ex.text}</td>
                        <td className="px-3 py-2">
                          <Badge variant="secondary">{ex.label}</Badge>
                        </td>
                        <td className="px-3 py-2">{ex.source}</td>
                        <td className="px-3 py-2">{ex.created_by ?? "—"}</td>
                        <td className="px-3 py-2">{new Date(ex.created_at).toLocaleDateString("fr-FR")}</td>
                        <td className="px-3 py-2">
                          <Button variant="ghost" size="sm" onClick={() => onDeleteExample(ex.id)}>
                            Supprimer
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {(examples?.items ?? []).length === 0 ? (
                      <tr>
                        <td className="px-3 py-4 text-muted-foreground" colSpan={6}>
                          Aucun exemple ajouté pour le moment.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Page {examples?.page ?? 1} / {examples?.pages ?? 1} — {examples?.total ?? 0} exemples
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(examples?.page ?? 1) <= 1}
                    onClick={() => setExamplesPage((p) => Math.max(1, p - 1))}
                  >
                    Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!examples || examples.page >= examples.pages}
                    onClick={() => setExamplesPage((p) => p + 1)}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
